import { User, ReadOnlyListing, UserSubscriptionReadOnly } from 'hotdesk_typescript';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { MyListing } from '../services/listing.service';

export type SubscriptionPassType = 'desk' | 'meetingRoom' | 'officeRoom';

export interface SubscriptionPassInfo {
    type: SubscriptionPassType;
    count: number;
    label?: string;
}

const promoLimit = (promo: { userLimit?: number } | undefined): number | null => {
    const limit = promo?.userLimit;
    return typeof limit === 'number' && limit > 0 ? limit : null;
};

const packagePromoLimit = (
    promos: Record<string, { userLimit?: number }> | undefined,
    key: string
): number | null => {
    const limit = promos?.[key]?.userLimit;
    return typeof limit === 'number' && limit > 0 ? limit : null;
};

export const getPassTypesFromUserSubscription = (
    userSub: UserSubscriptionReadOnly | undefined
): SubscriptionPassInfo[] => {
    if (!userSub) return [];

    const passes: SubscriptionPassInfo[] = [];
    const deskLimit = promoLimit(userSub.hotdeskPromo);
    if (deskLimit) passes.push({ type: 'desk', count: deskLimit });

    const meetingLimit = promoLimit(userSub.meetingRoomPromo);
    if (meetingLimit) passes.push({ type: 'meetingRoom', count: meetingLimit });

    const officeLimit = promoLimit(userSub.officePromo);
    if (officeLimit) passes.push({ type: 'officeRoom', count: officeLimit });

    if (passes.length > 0) return passes;

    const promos = userSub.package?.package?.promos as unknown as
        | Record<string, { userLimit?: number }>
        | undefined;

    const packageDesk = packagePromoLimit(promos, 'desk');
    if (packageDesk) passes.push({ type: 'desk', count: packageDesk });

    const packageMeeting = packagePromoLimit(promos, 'meetingRoom');
    if (packageMeeting) passes.push({ type: 'meetingRoom', count: packageMeeting });

    const packageOffice = packagePromoLimit(promos, 'officeRoom');
    if (packageOffice) passes.push({ type: 'officeRoom', count: packageOffice });

    return passes;
};

const NOTE_PATTERNS: { type: SubscriptionPassType; pattern: RegExp }[] = [
    { type: 'meetingRoom', pattern: /(\d+)\s*(?:x\s*)?meeting[\s-]*room/i },
    { type: 'officeRoom', pattern: /(\d+)\s*(?:x\s*)?(?:office[\s-]*room|private[\s-]*office)/i },
    { type: 'desk', pattern: /(\d+)\s*(?:x\s*)?desk/i },
];

export const getPassTypesFromListNotes = (listNotes: string[] = []): SubscriptionPassInfo[] => {
    const passes: SubscriptionPassInfo[] = [];

    for (const note of listNotes) {
        for (const { type, pattern } of NOTE_PATTERNS) {
            const match = note.match(pattern);
            if (match) {
                passes.push({
                    type,
                    count: parseInt(match[1], 10),
                    label: note.trim(),
                });
                break;
            }
        }
    }

    return passes;
};

export const resolveSubscriptionPassTypes = (options: {
    userSub?: UserSubscriptionReadOnly;
    listNotes?: string[];
    fallbackDescription?: string;
}): SubscriptionPassInfo[] => {
    const fromUser = getPassTypesFromUserSubscription(options.userSub);
    if (fromUser.length > 0) return fromUser;

    const fromNotes = getPassTypesFromListNotes(options.listNotes);
    if (fromNotes.length > 0) return fromNotes;

    if (options.fallbackDescription?.trim()) {
        return [{ type: 'desk', count: 0, label: options.fallbackDescription.trim() }];
    }

    return [];
};

export const findUserSubscriptionForPlan = (
    user: User | null,
    planName: string,
    packageId?: string
): UserSubscriptionReadOnly | undefined => {
    const subs = user?.userSubscriptions ?? [];
    if (!subs.length) return undefined;

    const packageIdNum = packageId ? parseInt(packageId, 10) : NaN;
    if (Number.isFinite(packageIdNum)) {
        const byPackage = subs.filter((sub) => sub.package?.id === packageIdNum);
        if (byPackage.length) {
            return pickOldestActiveUserSubscription(byPackage) ?? byPackage[0];
        }
    }

    const normalizedPlanName = planName.trim().toLowerCase();
    const byName = subs.filter(
        (sub) => sub.package?.name?.trim().toLowerCase() === normalizedPlanName
    );
    if (byName.length) {
        return pickOldestActiveUserSubscription(byName) ?? byName[0];
    }

    return pickOldestActiveUserSubscription(subs) ?? subs[0];
};

export type UserSubscriptionFilterOption = {
    packageName: string;
    subscriptionIds: string[];
    subscription: UserSubscriptionReadOnly;
};

export const groupUserSubscriptionsByPackageName = (
    subscriptions: UserSubscriptionReadOnly[] = []
): UserSubscriptionFilterOption[] => {
    const groups = new Map<string, UserSubscriptionFilterOption>();

    for (const subscription of subscriptions) {
        const subId = subscription.id?.toString();
        if (!subId) continue;

        const packageName = subscription.package?.name?.trim() || '';
        const key = packageName || `__id_${subId}`;
        const existing = groups.get(key);

        if (existing) {
            existing.subscriptionIds.push(subId);
            continue;
        }

        groups.set(key, {
            packageName,
            subscriptionIds: [subId],
            subscription
        });
    }

    return Array.from(groups.values());
};

/**
 * Only active & valid subscriptions should drive coverage/badging. The login
 * payload already filters to `is_active=True`, but persisted redux can hold
 * stale rows from a previous session — so we re-check `isActive`/`isValid` here.
 */
export const isActiveUserSubscription = (sub: {
    isActive?: boolean | null;
    isValid?: boolean | null;
}): boolean => sub.isActive !== false && sub.isValid !== false;

export const getActiveUserSubscriptions = (
    user: User | null
): UserSubscriptionReadOnly[] =>
    (user?.userSubscriptions || []).filter(isActiveUserSubscription);

type SubscriptionAgeFields = {
    id?: number | null;
    isActive?: boolean | null;
    isValid?: boolean | null;
    createdAt?: string | null;
};

const subscriptionCreatedAtMs = (createdAt?: string | null): number => {
    if (!createdAt) return Number.POSITIVE_INFINITY;
    const ms = new Date(createdAt).getTime();
    return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
};

const compareSubscriptionsOldestFirst = <T extends SubscriptionAgeFields>(
    a: T,
    b: T
): number => {
    const byCreatedAt =
        subscriptionCreatedAtMs(a.createdAt) -
        subscriptionCreatedAtMs(b.createdAt);
    if (byCreatedAt !== 0) return byCreatedAt;
    return (Number(a.id) || 0) - (Number(b.id) || 0);
};

/** Oldest purchased subscription first (`createdAt`, then `id`). */
export const sortSubscriptionsOldestFirst = <T extends SubscriptionAgeFields>(
    subs: T[]
): T[] => [...subs].sort(compareSubscriptionsOldestFirst);

/**
 * Subscriptions with passes first, oldest to newest. An explicitly exhausted
 * subscription is placed last; an unknown/loading balance keeps age order.
 */
export const sortSubscriptionsByPassesOldestFirst = <
    T extends SubscriptionAgeFields,
>(
    subs: T[],
    getRemainingPasses: (sub: T) => number | null | undefined
): T[] =>
    [...subs].sort((a, b) => {
        const aRemaining = getRemainingPasses(a);
        const bRemaining = getRemainingPasses(b);
        const aExhausted = aRemaining != null && aRemaining <= 0;
        const bExhausted = bRemaining != null && bRemaining <= 0;
        if (aExhausted !== bExhausted) return aExhausted ? 1 : -1;
        return compareSubscriptionsOldestFirst(a, b);
    });

/**
 * When a listing is covered by multiple active user subscriptions, bookings and
 * UI (card badge, details banner, promo application) must all use the same one:
 * the oldest active package by `createdAt` (then lowest `id`).
 *
 * Exception: when the user explicitly filtered explore to specific subscription
 * id(s) (`preferredIds`), prefer those over the global oldest so the card and
 * details stay aligned with the filter — even if that package is newer.
 */
export const pickOldestActiveUserSubscription = <T extends SubscriptionAgeFields>(
    subscriptions: T[] = [],
    options?: {
        applicableIds?: Array<number | string> | null;
        /** Explore filter / deep-link preference; wins over global oldest. */
        preferredIds?: Array<number | string> | null;
        /** When true, return undefined if no applicable id matches. */
        requireApplicableMatch?: boolean;
    }
): T | undefined => {
    const active = subscriptions.filter(isActiveUserSubscription);
    if (!active.length) return undefined;

    const toIdSet = (ids?: Array<number | string> | null) =>
        new Set(
            (ids ?? [])
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id))
        );

    const applicableSet = toIdSet(options?.applicableIds);
    const hasApplicable = applicableSet.size > 0;

    const inApplicable = (sub: T) =>
        !hasApplicable ||
        (sub.id != null && applicableSet.has(Number(sub.id)));

    const preferredSet = toIdSet(options?.preferredIds);
    if (preferredSet.size > 0) {
        const preferred = active.filter(
            (sub) =>
                sub.id != null &&
                preferredSet.has(Number(sub.id)) &&
                inApplicable(sub)
        );
        if (preferred.length) {
            return [...preferred].sort(compareSubscriptionsOldestFirst)[0];
        }
    }

    if (hasApplicable) {
        const matched = active.filter(
            (sub) => sub.id != null && applicableSet.has(Number(sub.id))
        );
        if (matched.length) {
            return [...matched].sort(compareSubscriptionsOldestFirst)[0];
        }
        if (options?.requireApplicableMatch) return undefined;
    }

    return [...active].sort(compareSubscriptionsOldestFirst)[0];
};

/**
 * Ids from an active explore subscription filter. `null`/opt-out/empty → no
 * preference (callers fall back to oldest). Non-empty → prefer those ids.
 */
export const getPreferredUserSubscriptionIds = (
    activeSubscriptionIdIn?: string | string[] | null
): string[] | undefined => {
    const ids = getActiveSubscriptionIds(activeSubscriptionIdIn);
    if (ids === null || ids.length === 0) return undefined;
    return ids;
};

/** Query param for carrying a filtered subscription into listing details. */
export const PREFER_USER_SUBSCRIPTION_ID_PARAM = 'preferUserSubscriptionId';

type MembershipPlanNamed = {
    package?: { name?: string } | null;
    subscription?: { alias?: string } | null;
    createdAt?: string | null;
    validUntil?: string | null;
};

export const getMembershipPlanName = (
    sub: MembershipPlanNamed,
    fallback: string
): string => sub.package?.name || sub.subscription?.alias || fallback;

/** Latest subscription per plan name — same ordering as My Memberships. */
export const dedupeSubscriptionsByPlanName = <T extends MembershipPlanNamed>(
    subs: T[],
    fallback: string,
    dateKey: 'createdAt' | 'validUntil' = 'validUntil'
): T[] => {
    const ascending = [...subs].sort(
        (a, b) =>
            new Date(a[dateKey] ?? 0).getTime() -
            new Date(b[dateKey] ?? 0).getTime()
    );
    const latestByName = new Map<string, T>();
    for (const sub of ascending) {
        latestByName.set(getMembershipPlanName(sub, fallback), sub);
    }
    return [...latestByName.values()].sort(
        (a, b) =>
            new Date(b[dateKey] ?? 0).getTime() -
            new Date(a[dateKey] ?? 0).getTime()
    );
};

export const getUserSubscriptionIds = (user: User | null): string[] => {
    return getActiveUserSubscriptions(user)
        .map((s) => s.id?.toString())
        .filter(Boolean) as string[];
};

export const getUserSubscriptionIdIn = (user: User | null): string =>
    getUserSubscriptionIds(user).join(',');

/**
 * "Applied" badge: returns the package name of the user's subscription that
 * covers this workspace/listing, or null.
 *
 * Source of truth is `listing.metaData.applicableSubscriptions` — for
 * authenticated requests the backend tags each row with the ids of the user's
 * own subscriptions whose promo actually covers it (promo-based, per-user). This
 * is consistent on the landing and explore, so we use it exclusively rather than
 * the login `package` id lists (which don't match the promo coverage) or an
 * active-filter heuristic (which wrongly badges everything on unfiltered views).
 *
 * A non-empty list means "covered by one of your subscriptions". We resolve the
 * plan name from the referenced subscription, falling back to the active
 * subscription's name (ids can drift between login and this payload, but the
 * plan name is stable).
 */
export const getCoveredSubscriptionName = (
    user: User | null,
    variant: 'workspace' | 'listing',
    listing?: MyListing | null,
    coveredWorkspaceIds: number[] = [],
    coveredListingIds: number[] = [],
    activeSubscriptionIdIn?: string | string[] | null
): string | null => {
    if (!user?.id || !listing) return null;

    const meta = listing.metaData as
        | {
              applicableSubscriptions?: number[];
              subscriptionApplicable?: boolean;
          }
        | undefined;
    const applicable = meta?.applicableSubscriptions;
    let covered = false;

    if (variant === 'listing') {
        // Listing cards must match this exact listing. Never inherit workspace
        // coverage — a covered desk does not make a meeting room applicable.
        if (meta?.subscriptionApplicable === false) {
            return null;
        }
        const inListingCoverage =
            listing.id != null &&
            coveredListingIds.includes(Number(listing.id));
        covered =
            inListingCoverage ||
            meta?.subscriptionApplicable === true ||
            !!applicable?.length;
    } else {
        // Fast path: the backend tagged this exact row. Workspace cards also
        // fall back to the promo-based coverage set — the distinct=workspace
        // representative listing often isn't the covered one.
        covered = !!applicable?.length;
        if (!covered) {
            const wsId = listing.workspace?.id ?? listing.id;
            covered =
                wsId != null && coveredWorkspaceIds.includes(Number(wsId));
        }
    }
    if (!covered) return null;

    const preferredIds = getPreferredUserSubscriptionIds(activeSubscriptionIdIn);
    const activeSubs = getActiveUserSubscriptions(user);
    const picked = pickOldestActiveUserSubscription(activeSubs, {
        applicableIds: applicable,
        preferredIds,
        requireApplicableMatch: variant === 'listing' && !!applicable?.length
    });
    return picked?.package?.name || null;
};

/** Explore-only: empty string means user opted out of subscription filter. */
export const EXPLORE_SUBSCRIPTION_OPT_OUT = '';

export const mergeSubscriptionIntoExploreFilters = (
    fromUrl: Record<string, string>,
    existingFilters: Record<string, string>,
    userSubscriptionIds: string[] = []
): Record<string, string> => {
    // A `userSubscriptionIdIn` value is only meaningful for subscriptions the
    // current user actually owns. This strips stale ids left in the URL or in
    // persisted redux from a previous (logged-in) session — e.g. a logged-out
    // user landing on `?userSubscriptionIdIn=5851`. Returns the retained id
    // string, the opt-out sentinel, or undefined when nothing valid remains.
    const retainOwned = (value?: string): string | undefined => {
        if (value === undefined) return undefined;
        if (value === EXPLORE_SUBSCRIPTION_OPT_OUT) {
            return EXPLORE_SUBSCRIPTION_OPT_OUT;
        }
        const owned = value
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id && userSubscriptionIds.includes(id));
        return owned.length ? owned.join(',') : undefined;
    };

    if (fromUrl.userSubscriptionIdIn !== undefined) {
        const retained = retainOwned(fromUrl.userSubscriptionIdIn);
        if (retained === undefined) {
            const { userSubscriptionIdIn: _drop, ...rest } = fromUrl;
            return rest;
        }
        return { ...fromUrl, userSubscriptionIdIn: retained };
    }
    if (existingFilters.userSubscriptionIdIn === EXPLORE_SUBSCRIPTION_OPT_OUT) {
        return { ...fromUrl, userSubscriptionIdIn: EXPLORE_SUBSCRIPTION_OPT_OUT };
    }
    const retainedExisting = retainOwned(existingFilters.userSubscriptionIdIn);
    if (
        retainedExisting !== undefined &&
        retainedExisting !== EXPLORE_SUBSCRIPTION_OPT_OUT
    ) {
        return { ...fromUrl, userSubscriptionIdIn: retainedExisting };
    }
    // No default subscription filter: show everything and mark covered items
    // "applied" via getCoveredSubscriptionName instead of filtering results.
    return fromUrl;
};

export const getActiveSubscriptionIds = (
    activeSubscriptionIdIn?: string | string[] | null
): string[] | null => {
    if (
        activeSubscriptionIdIn === EXPLORE_SUBSCRIPTION_OPT_OUT ||
        activeSubscriptionIdIn === ''
    ) {
        return [];
    }
    if (activeSubscriptionIdIn == null) {
        return null;
    }
    if (Array.isArray(activeSubscriptionIdIn)) {
        return activeSubscriptionIdIn.map(String).filter(Boolean);
    }
    return activeSubscriptionIdIn.split(',').filter(Boolean);
};

export const listingMatchesActiveSubscriptionFilter = (
    listing: ReadOnlyListing,
    activeSubscriptionIdIn?: string
): boolean => {
    const activeIds = getActiveSubscriptionIds(activeSubscriptionIdIn);
    if (activeIds === null || activeIds.length === 0) {
        return true;
    }

    const meta = listing.metaData as
        | { applicableSubscriptions?: number[] }
        | undefined;
    const applicable = meta?.applicableSubscriptions ?? [];
    if (!applicable.length) {
        return false;
    }

    const activeSet = new Set(activeIds);
    return applicable.some((id) => activeSet.has(String(id)));
};

export const buildExploreFetchParams = (options: {
    page: number;
    limit?: number;
    type: 'country' | 'city' | 'workspace';
    country?: string;
    city?: string;
    geoLocation?: GeoCodeResult | null;
}): {
    page: number;
    limit?: number;
    lastSearch?: GeoCodeResult;
    country?: string;
    city?: string;
} => {
    const params: {
        page: number;
        limit?: number;
        lastSearch?: GeoCodeResult;
        country?: string;
        city?: string;
    } = { page: options.page };

    if (options.limit != null) {
        params.limit = options.limit;
    }
    if (options.type === 'country' && options.country) {
        params.country = options.country;
    } else if (options.type === 'city' && options.country && options.city) {
        params.country = options.country;
        params.city = options.city;
    } else if (options.geoLocation) {
        params.lastSearch = options.geoLocation;
    }

    return params;
};

export const getSubscriptionName = (
    user: User | null,
    listing?: MyListing,
    activeSubscriptionIdIn?: string | string[] | null
): string | null => {
    if (!user?.id) return null;

    const activeIds = getActiveSubscriptionIds(activeSubscriptionIdIn);
    if (activeIds !== null && activeIds.length === 0) {
        return null;
    }

    const candidates = getActiveUserSubscriptions(user).filter((sub) => {
        const subId = sub.id?.toString();
        if (activeIds !== null && subId && !activeIds.includes(subId)) {
            return false;
        }
        return true;
    });

    if ((listing?.metaData?.applicableSubscriptions?.length ?? 0) > 0) {
        const oldest = pickOldestActiveUserSubscription(candidates, {
            applicableIds: listing?.metaData?.applicableSubscriptions,
            requireApplicableMatch: true
        });
        return oldest?.package?.name || null;
    }
    if (!listing) {
        return (
            pickOldestActiveUserSubscription(candidates)?.package?.name || null
        );
    }
    return null;
};

export type SubscriptionOverageBreakdown = {
    unitPrice: number;
    /** Time units on the paid overage line (hours/days). */
    overageUnits: number;
    /** Seat/guest factor on the paid overage line (1 = omit). */
    seats: number;
    /** Time units covered by membership passes. */
    coveredUnits: number;
    /** Seat/guest factor covered by membership passes (1 = omit). */
    coveredSeats: number;
    /** Pass count consumed from the membership. */
    coveredPasses: number;
};

const almostWhole = (n: number): boolean =>
    Number.isFinite(n) && Math.abs(n - Math.round(n)) < 1e-6;

const roundUnits = (n: number): number =>
    almostWhole(n) ? Math.round(n) : Math.round(n * 100) / 100;

export type MembershipBreakdownOptions = {
    /** Promo cap per pass (when lower than unit price). */
    promoCap?: number | null;
    /** Pass balance before this booking is applied. */
    remainingPassesBefore?: number | null;
};

const inferPassesFromDiscount = (
    passDiscount: number,
    unitPrice: number,
    promoCap: number | null | undefined,
    bookedPasses: number
): number | null => {
    if (!(passDiscount > 0) || !(bookedPasses > 0) || !(unitPrice > 0)) {
        return null;
    }
    const creditPerPass =
        promoCap != null && promoCap > 0 && promoCap < unitPrice
            ? promoCap
            : unitPrice;
    const fromDiscount = Math.round(passDiscount / creditPerPass);
    if (fromDiscount > 0 && fromDiscount <= bookedPasses) {
        return fromDiscount;
    }
    return null;
};

/**
 * Passes consumed for a membership booking: one pass per booked unit × seat,
 * capped by the user's remaining balance. When pass balance runs out mid-
 * booking, only the covered portion counts. Promo cap limits $ credit, not
 * how many passes attach to booked units (unless balance is insufficient).
 */
/**
 * The pass count the quote itself reports (`details.couponsConsumed`), or
 * `null` when the quote carries none.
 *
 * The BE is the authority on how many passes a booking draws - it applies
 * the cap at the passes left and returns the count. The price-based
 * inference below predates that field and misreads a capped booking (11
 * seats against 9 passes read "x0 passes" next to a -135 EGP line), so the
 * quoted count wins whenever it is present.
 */
const quotedPassesConsumed = (
    couponsConsumed: number | null | undefined,
    bookedPasses: number
): number | null => {
    if (couponsConsumed == null || !Number.isFinite(couponsConsumed)) return null;
    if (couponsConsumed < 0) return null;
    return Math.min(Math.round(couponsConsumed), bookedPasses);
};

const resolveMembershipPassesConsumed = (options: {
    bookedPasses: number;
    finalPrice: number;
    unitPrice: number;
    remainingAfter?: number | null;
    remainingBefore?: number | null;
    passDiscount?: number | null;
    promoCap?: number | null;
}): number => {
    const {
        bookedPasses,
        finalPrice,
        unitPrice,
        remainingAfter,
        remainingBefore,
        passDiscount,
        promoCap,
    } = options;

    if (!(bookedPasses > 0)) return 0;

    let consumed = bookedPasses;

    // Paying for whole uncovered time units at the listing rate (ran out of passes).
    if (unitPrice > 0) {
        const unpaidUnits = finalPrice / unitPrice;
        if (
            unpaidUnits >= 1 &&
            almostWhole(unpaidUnits) &&
            unpaidUnits < bookedPasses
        ) {
            consumed = Math.max(0, bookedPasses - Math.round(unpaidUnits));
        }
    }

    // Quote still has passes left → all booked units drew a pass.
    if (
        remainingAfter != null &&
        Number.isFinite(remainingAfter) &&
        remainingAfter > 0
    ) {
        consumed = bookedPasses;
    }

    // Cap at known balance before booking (profile / applicable sub).
    if (
        remainingBefore != null &&
        Number.isFinite(remainingBefore) &&
        remainingBefore >= 0
    ) {
        consumed = Math.min(consumed, remainingBefore, bookedPasses);
    } else if (
        remainingAfter === 0 &&
        passDiscount != null &&
        passDiscount > 0
    ) {
        // Profile balance may already be post-quote (0) — infer from discount.
        const fromDiscount = inferPassesFromDiscount(
            passDiscount,
            unitPrice,
            promoCap,
            bookedPasses
        );
        if (fromDiscount != null) {
            consumed = Math.min(consumed, fromDiscount);
        }
    }

    return Math.max(0, Math.round(consumed));
};

/** Split pass-units into a period × seats display, preferring fewer seats over fractional days. */
const splitPassUnits = (
    passUnits: number,
    period: number,
    bookedSeats: number
): { units: number; seats: number } => {
    if (!(passUnits > 0)) return { units: 0, seats: 0 };

    if (bookedSeats > 1 && period > 0) {
        const seatsForPeriod = passUnits / period;
        if (almostWhole(seatsForPeriod) && seatsForPeriod > 0) {
            return { units: period, seats: Math.round(seatsForPeriod) };
        }
        const periodForSeats = passUnits / bookedSeats;
        if (almostWhole(periodForSeats) && periodForSeats > 0) {
            return { units: Math.round(periodForSeats), seats: bookedSeats };
        }
    }

    return {
        units: roundUnits(passUnits / (bookedSeats > 1 ? bookedSeats : 1)),
        seats: bookedSeats > 1 ? bookedSeats : 1,
    };
};

/**
 * Membership bookings that exceed remaining passes charge only the uncovered
 * portion (`finalPrice / unitPrice` pass-units).
 *
 * Prefer keeping the booking's period and reducing seats when that divides
 * evenly (1 Day × 6 seats with 3 passes left → covered `x 1 Day × 3 seats`,
 * overage `x 1 Day × price × 3 seats`), otherwise reduce the time units
 * (6h with 4 passes left → covered `x 4 hours`, overage `x 2 hours × price`).
 */
export function getSubscriptionOverageBreakdown(
    details:
        | {
              finalPrice?: number | null;
              unitPrice?: number | null;
              period?: number | null;
              seats?: number | null;
              remainingUsage?: number | null;
              couponsConsumed?: number | null;
          }
        | null
        | undefined,
    options?: MembershipBreakdownOptions
): SubscriptionOverageBreakdown | null {
    if (!details) return null;
    const finalPrice = Number(details.finalPrice);
    if (!Number.isFinite(finalPrice) || !(finalPrice > 0)) return null;

    const bookedSeats = details.seats && details.seats > 0 ? details.seats : 1;
    const period = details.period && details.period > 0 ? details.period : 0;
    const bookedPasses = period * bookedSeats;
    const unitPrice = Number(details.unitPrice);

    if (Number.isFinite(unitPrice) && unitPrice > 0 && period > 0) {
        const coveredPasses =
            quotedPassesConsumed(details.couponsConsumed, bookedPasses) ??
            resolveMembershipPassesConsumed({
                bookedPasses,
                finalPrice,
                unitPrice,
                remainingAfter: details.remainingUsage,
                remainingBefore: options?.remainingPassesBefore,
                promoCap: options?.promoCap,
            });
        const overagePasses = Math.max(0, bookedPasses - coveredPasses);
        const overage = splitPassUnits(overagePasses, period, bookedSeats);
        const covered = splitPassUnits(coveredPasses, period, bookedSeats);

        return {
            unitPrice,
            overageUnits: overage.units,
            seats: overage.seats,
            coveredUnits: covered.units,
            coveredSeats: covered.seats,
            coveredPasses,
        };
    }

    // Quote omitted unitPrice — can't isolate uncovered units; fall back to
    // the full booking with a derived rate so the line still adds up.
    if (period > 0) {
        return {
            unitPrice: finalPrice / bookedPasses,
            overageUnits: period,
            seats: bookedSeats,
            coveredUnits: 0,
            coveredSeats: 0,
            coveredPasses: 0,
        };
    }
    return null;
}

export type MembershipRequestChargesBreakdown = {
    requestCharges: number;
    passCount: number;
    passDiscount: number;
    serviceFee: number;
    finalPrice: number;
    remainingPasses: number;
    period: number;
    seats: number;
    unitPrice: number;
};

/**
 * Receipt breakdown when a membership pass partially covers a booking and the
 * user still owes money — "Request Charges" style (gross → pass credit → fees).
 */
export function getMembershipRequestChargesBreakdown(
    details:
        | {
              finalPrice?: number | null;
              unitPrice?: number | null;
              period?: number | null;
              seats?: number | null;
              remainingUsage?: number | null;
              transactionValue?: number | null;
              discountValue?: number | null;
              userSubscription?: number | null;
              couponsConsumed?: number | null;
          }
        | null
        | undefined,
    vatRate = 0,
    options?: MembershipBreakdownOptions
): MembershipRequestChargesBreakdown | null {
    if (!details?.userSubscription) return null;
    const finalPrice = Number(details.finalPrice ?? 0);
    if (!Number.isFinite(finalPrice) || !(finalPrice > 0)) return null;

    const period = details.period && details.period > 0 ? details.period : 0;
    const seats = details.seats && details.seats > 0 ? details.seats : 1;
    const unitPrice = Number(details.unitPrice ?? 0);
    const requestCharges =
        unitPrice > 0 && period > 0
            ? unitPrice * period * seats
            : Number(details.transactionValue ?? 0);

    const bookedPasses = period * seats;

    const discountValue = Number(details.discountValue ?? 0);
    const vatAmount = requestCharges * vatRate;
    const serviceFee = Math.max(
        0,
        finalPrice - requestCharges - vatAmount + discountValue
    );
    const passDiscount = Math.max(0, requestCharges - (finalPrice - serviceFee));

    const passCount =
        quotedPassesConsumed(details.couponsConsumed, bookedPasses) ??
        resolveMembershipPassesConsumed({
            bookedPasses,
            finalPrice,
            unitPrice,
            remainingAfter: details.remainingUsage,
            remainingBefore: options?.remainingPassesBefore,
            passDiscount,
            promoCap: options?.promoCap,
        });

    return {
        requestCharges,
        passCount,
        passDiscount,
        serviceFee,
        finalPrice,
        remainingPasses: Number(details.remainingUsage ?? 0),
        period,
        seats,
        unitPrice,
    };
}
